import { useLocalStorage } from '@uidotdev/usehooks';
import { useEffect, useState } from 'react';

import getCustomer from '@/shared/api/customers/getCustomer';
import getOrders from '@/shared/api/customers/getOrders';
import { LocalStorageKeys } from '@/shared/configs/localStorageService';
import useRedirectLogin from '@/shared/hooks/useRedirectLogin';
import Customer from '@/shared/types/customer.type';
import Order from '@/shared/types/order/order.type';
import OrderPayment from '@/shared/types/order/orderPayment.type';

type Args = {
  customerId: string;
};

const useCustomerEditData = ({ customerId }: Args) => {
  const redirectLogin = useRedirectLogin();

  const [customer, setCustomer] = useState<Customer | null>(null);
  const [orders, setOrders] = useState<Order[] | null>(null);
  const [payments, setPayments] = useState<OrderPayment[] | null>(null);

  useLocalStorage(LocalStorageKeys.Filters, {
    orders: {
      search: [
        {
          in_customer_id: {
            value: customerId,
            cop: ['='],
            op: undefined,
          },
        },
      ],
    },
  });

  useEffect(() => {
    const fetchCustomer = async () => {
      const response = await getCustomer(customerId, () => redirectLogin());

      if (response.success) {
        setCustomer(response.data[0]);
      } else {
        console.error(
          'Error occurred while fetching customer data:',
          response.msg,
        );
      }
    };

    void fetchCustomer();
  }, [customerId, redirectLogin]);

  useEffect(() => {
    const fetchOrders = async () => {
      const response = await getOrders(customerId, () => redirectLogin());

      if (response.success) {
        const orders = response.data;
        const payments = orders.map((order) => order.payments).flat();

        setOrders(orders);
        setPayments(payments);
      } else {
        console.error(
          'Error occurred while fetching orders data:',
          response.msg,
        );
      }
    };

    void fetchOrders();
  }, [customerId, redirectLogin]);

  return {
    customer,
    orders,
    payments,
  };
};

export default useCustomerEditData;
